/**
 * Layout A: Farm Selector Layout
 * Full-page centered layout with no sidebar, minimal top bar.
 * Used for farm selector home, profile, admin pages.
 */

import React from 'react';
import { Box, AppBar, Toolbar, Typography, CssBaseline, useMediaQuery, useTheme} from '@mui/material';
import { WaterDrop} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TopBarActions from './TopBarActions';
import ThemeToggle from '@/components/theme/ThemeToggle';
import { useLayoutServices } from '@/hooks/useLayoutServices';
import { tokens } from '@/theme/tokens';
import SaqiaLogo from './SaqiaLogo';

const FarmSelectorLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation(['common'], { useSuspense: false });
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  useLayoutServices();

  // width + minWidth: 0 keep the root flex item (child of the App-level flex
  // wrapper) from inflating to its content's min-width on small screens.
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', width: '100%', minWidth: 0 }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: 'background.paper',
          color: 'text.primary',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Toolbar>
          {isMobile ? (
            /* Mobile: theme toggle on left, actions on right */
            <>
              <ThemeToggle />
              <Box sx={{ flexGrow: 1 }} />
              <TopBarActions compact />
            </>
          ) : (
            /* Desktop: logo on left, actions on right */
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }} onClick={() => navigate('/')}>
                <SaqiaLogo color={theme.palette.text.primary} />
              </Box>
              <Box sx={{ flexGrow: 1 }} />
              <TopBarActions />
            </>
          )}
        </Toolbar>
      </AppBar>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          pt: { xs: '56px', sm: '64px' },
          minHeight: '100vh',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default FarmSelectorLayout;
